import { supabase } from '../utils/supabase';
import { detectGameType } from '../utils/gameTypeUtils';
import { detectPlayerResult } from '../utils/gameResultDetector';
import { computeLateJoinerPenaltySuggestion } from '../utils/lateJoinerPenaltyUtils';
import { triggerSessionCompletionEffects } from './sessionCompletionService';
import { normalizePegBoardQueueReturn } from '../utils/pegBoard/constants';

// Helper function to get game type ID
const getGameTypeId = (team1Players, team2Players) => {
  const gameType = detectGameType(team1Players, team2Players);
  return gameType.id; // Extract the ID from the object
};

/**
 * Creates a new session in the database
 * @param {Object} sessionData - Session data
 * @param {string} sessionData.clubId - Club ID
 * @param {string} sessionData.name - Session name
 * @param {string} sessionData.description - Session description
 * @param {string} sessionData.hostId - Host player ID
 * @returns {Object} Created session data
 */
export const createSession = async (sessionData) => {
  try {
    console.log('Creating session with data:', sessionData);
    
    const { data, error } = await supabase
      .from('sessions')
      .insert([
        {
          club_id: sessionData.clubId,
          name: sessionData.name,
          description: sessionData.description,
          created_by: sessionData.createdById,
          session_date: new Date().toISOString().split('T')[0], // Extract date part only
          start_time: new Date().toTimeString().split(' ')[0], // Extract time part only (HH:MM:SS)
          end_time: sessionData.endTime || null, // Custom session end time
          season_id: sessionData.seasonId,
          session_status: 'ongoing',
          max_players: sessionData.maxPlayers === null ? null : (sessionData.maxPlayers ?? 16),
          current_players: 0, // Will be updated by trigger when players are added
          waitlist_enabled: sessionData.waitlistEnabled || true, // Enable waitlist by default for custom sessions
          waitlist_promotion_type: sessionData.waitlistPromotionType || 'first_come_first_served',
          // Custom session specific fields
          number_of_courts: sessionData.numberOfCourts ?? null,
          location: sessionData.location || null,
          session_type_id: sessionData.sessionTypeId || null,
          is_public: sessionData.isPublic || false,
          game_type_restriction: sessionData.game_type_restriction || 'all_types',
          enable_player_categorisation: sessionData.enable_player_categorisation || false,
          competitive_match_format: sessionData.competitive_match_format || 'doubles',
          points_target: sessionData.points_target ?? 21,
          deuce_mode: sessionData.deuce_mode || 'standard',
          peg_board_selection_window: sessionData.peg_board_selection_window ?? 6,
          peg_board_queue_return: normalizePegBoardQueueReturn(sessionData.peg_board_queue_return),
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('Supabase error creating session:', error);
      throw error;
    }
    
    console.log('Session created successfully:', data);
    return data;
  } catch (error) {
    console.error('Error creating session:', error);
    throw error;
  }
};

/**
 * Creates a new game in the session_games table using direct database calls
 * @param {Object} gameData - Game data
 * @param {string} gameData.sessionId - Session ID
 * @param {string} gameData.clubId - Club ID
 * @param {number} gameData.team1Score - Team 1 score
 * @param {number} gameData.team2Score - Team 2 score
 * @param {number} gameData.winningTeam - Winning team (1 or 2)
 * @param {Array} gameData.team1Players - Team 1 player objects (with gender info)
 * @param {Array} gameData.team2Players - Team 2 player objects (with gender info)
 * @param {Date} gameData.startTime - Game start time
 * @param {Date} gameData.endTime - Game end time
 * @param {string|null} [gameData.gameCreateMethod] - How the game was created (matchmaking, manual, etc.)
 * @param {string|null} [gameData.matchmakingAlgorithm] - Matchmaking algorithm identifier when applicable
 * @param {Object} playersById - Players by ID
 * @param {boolean} testMode - If true, disable error handling for testing
 * @returns {Object} Created game data
 */
export const createGame = async (gameData, playersById, testMode = false, partnerships = []) => {
  try {
    console.log('[GAME] Creating game with data:', gameData);
    console.log('[GAME] Test mode:', testMode ? 'ENABLED - error handling disabled' : 'DISABLED - error handling enabled');
    console.log('[GAME] Players by ID keys:', Object.keys(playersById));
    
    // Make sure team1Players and team2Players are arrays
    if (!Array.isArray(gameData.team1Players)) {
      console.error('[GAME] team1Players is not an array:', gameData.team1Players);
      gameData.team1Players = [];
    }
    
    if (!Array.isArray(gameData.team2Players)) {
      console.error('[GAME] team2Players is not an array:', gameData.team2Players);
      gameData.team2Players = [];
    }
    
    // Use direct database calls
    console.log('[GAME] Using direct database calls');
    return createGameDirect(gameData, playersById);
  } catch (error) {
    console.error('[GAME] Error in createGame:', error);
    
    if (testMode) {
      throw error;
    }
    
    console.log('[GAME] Retrying with error handling');
    
    // Retry with error handling
    return createGameDirect(gameData, playersById);
  }
};

/**
 * Create a game using direct database calls
 * @param {Object} gameData - Game data
 * @param {Object} playersById - Players by ID (can be {} when passing full player objects)
 * @returns {Object} Created game data
 */
export const createGameDirect = async (gameData, playersById = {}, partnerships = []) => {
  // Declare game variable at the function scope so it's available in the catch block
  let game = null;
  try {
    // Normalize property names (support both camelCase and snake_case)
    const team1Score = gameData.team_1_score ?? gameData.team1Score;
    const team2Score = gameData.team_2_score ?? gameData.team2Score;
    const seasonId = gameData.season_id ?? gameData.seasonId;

    // Using direct database method to create game

    // Get full player objects with gender information
    const team1PlayerObjs = gameData.team1Players.map(p => typeof p === 'string' ? playersById[p] : p);
    const team2PlayerObjs = gameData.team2Players.map(p => typeof p === 'string' ? playersById[p] : p);
    
    // Detect game type based on player genders (use provided gameTypeId when set, e.g. for tournaments)
    const detectedGameTypeId = getGameTypeId(team1PlayerObjs, team2PlayerObjs);
    const gameTypeId = gameData.gameTypeId || detectedGameTypeId || 2; // Default to Levels Doubles (ID 2)
    // Use detected game type or fallback to default

    // Check session type if sessionId is provided - social/private sessions always use self_reported
    let finalGameCreateMethod = gameData.gameCreateMethod || null;
    if (gameData.sessionId) {
      try {
        const { data: sessionData } = await supabase
          .from('sessions')
          .select('session_types(name)')
          .eq('id', gameData.sessionId)
          .single();
        
        if (sessionData?.session_types?.name === 'Social Session' || 
            sessionData?.session_types?.name === 'Private Session') {
          finalGameCreateMethod = 'self_reported';
        }
      } catch (sessionError) {
        // If we can't fetch session type, continue with provided gameCreateMethod
        console.warn('[GAME] Could not fetch session type, using provided gameCreateMethod:', sessionError);
      }
    }

    // Validate essential data before insertion
    // Note: sessionId can be null for external games (self_reported) and tournament games
    if (gameData.sessionId === undefined && finalGameCreateMethod !== 'self_reported' && finalGameCreateMethod !== 'tournament') {
      throw new Error('Missing required field: sessionId');
    }
    
    if (typeof team1Score !== 'number' || typeof team2Score !== 'number') {
      throw new Error('Invalid score data types');
    }

    // First, create the game record
    const { data: game, error: gameError } = await supabase
      .from('session_games')
      .insert([
        {
          session_id: gameData.sessionId ?? null,  // Can be null for external/tournament games
          club_id: gameData.clubId || null,       // Can be null for private/external games
          team_1_score: team1Score,
          team_2_score: team2Score,
          winning_team: gameData.winningTeam,
          start_time: gameData.startTime,
          end_time: gameData.endTime,
          game_type_id: gameTypeId,
          season_id: seasonId || null,  // Can be null
          game_create_method: finalGameCreateMethod,
          matchmaking_algorithm: gameData.matchmakingAlgorithm || null,
          tournament_match_id: gameData.tournamentMatchId || null,  // For tournament games
          game_order: gameData.gameOrder || null  // Game number in best-of-N match
          // Note: URLs and notes are stored in game_contexts table, not here
        }
      ])
      .select()
      .single();

    if (gameError) {
      console.error('[GAME] Supabase error creating game:', gameError);
      console.error('[GAME] Error details:', {
        code: gameError.code,
        message: gameError.message,
        details: gameError.details,
        hint: gameError.hint
      });
      
      // Provide more specific error messages
      if (gameError.code === '23505') {
        throw new Error(`Duplicate game record detected: ${gameError.message}`);
      } else if (gameError.code === '23503') {
        throw new Error(`Foreign key constraint violation: ${gameError.message}`);
      } else if (gameError.code === '42703') {
        throw new Error(`Column does not exist: ${gameError.message}`);
      } else {
        throw new Error(`Database error (${gameError.code}): ${gameError.message}`);
      }
    }

    // Game created successfully

    // Create player-game records directly
    const playerGameRecords = [
      // Team 1 players
      ...gameData.team1Players.map(playerId => ({
        session_game_id: game.id,
        player_id: typeof playerId === 'string' ? playerId : playerId.id,
        team_number: 1,
        result: detectPlayerResult(1, gameData.winningTeam),
        season_id: seasonId,
        club_id: gameData.clubId
      })),
      // Team 2 players
      ...gameData.team2Players.map(playerId => ({
        session_game_id: game.id,
        player_id: typeof playerId === 'string' ? playerId : playerId.id,
        team_number: 2,
        result: detectPlayerResult(2, gameData.winningTeam),
        season_id: seasonId,
        club_id: gameData.clubId
      }))
    ];

    console.log('[GAME] Creating player-game records:', playerGameRecords.length, 'records');
    
    const { data: playerGameData, error: playerGameError } = await supabase
      .from('players_session_games')
      .insert(playerGameRecords)
      .select();

    if (playerGameError) {
      console.error('[GAME] Error creating player-game records:', playerGameError);
      throw new Error(`Failed to create player-game records: ${playerGameError.message}`);
    }

    console.log('[GAME] Player-game records created successfully:', playerGameData);
    if (game) {
      // Check if this is a social or private session - skip competitive-only features
      let isSocialOrPrivateSession = false;
      if (gameData.sessionId) {
        try {
          const { data: sessionData } = await supabase
            .from('sessions')
            .select('session_types(name)')
            .eq('id', gameData.sessionId)
            .single();
          
          if (sessionData?.session_types?.name === 'Social Session' || 
              sessionData?.session_types?.name === 'Private Session') {
            isSocialOrPrivateSession = true;
          }
        } catch (sessionError) {
          // If we can't fetch session type, assume competitive (safer default)
          console.warn('[GAME] Could not fetch session type, assuming competitive session:', sessionError);
        }
      }
      
      // Log player interactions for matchmaking penalties (only for competitive sessions)
      // This tracks how many times players have played together/against each other
      // Skip for: social/private sessions, tournament games (no session), or games without sessionId
      const isTournamentGame = finalGameCreateMethod === 'tournament';
      const hasSession = gameData.sessionId != null;
      if (!isSocialOrPrivateSession && !isTournamentGame && hasSession) {
        try {
          const { batchUpdatePlayerInteractions } = await import('./sessionStateService');
          
          // Normalize player IDs to strings
          const team1Ids = gameData.team1Players.map(p => typeof p === 'string' ? p : p.id);
          const team2Ids = gameData.team2Players.map(p => typeof p === 'string' ? p : p.id);
          const team1Set = new Set(team1Ids);
          const team2Set = new Set(team2Ids);
          const allPlayerIds = [...team1Ids, ...team2Ids];
          
          // Create pairs: all combinations of players (teammates and opponents)
          // Track both total interactions and same-team interactions separately
          const playerPairs = [];
          for (let i = 0; i < allPlayerIds.length; i++) {
            for (let j = i + 1; j < allPlayerIds.length; j++) {
              const player1Id = allPlayerIds[i];
              const player2Id = allPlayerIds[j];
              
              // Determine if they're on the same team
              const isSameTeam = 
                (team1Set.has(player1Id) && team1Set.has(player2Id)) ||
                (team2Set.has(player1Id) && team2Set.has(player2Id));
              
              playerPairs.push({
                player1Id,
                player2Id,
                isSameTeam
              });
            }
          }
          
          if (playerPairs.length > 0) {
            await batchUpdatePlayerInteractions(gameData.sessionId, playerPairs);
            const sameTeamCount = playerPairs.filter(p => p.isSameTeam).length;
            console.log('[GAME] Player interactions logged:', playerPairs.length, 'pairs', `(${sameTeamCount} same-team, ${playerPairs.length - sameTeamCount} opponents)`);
          }
        } catch (error) {
          console.warn('[GAME] Failed to log player interactions (non-critical):', error);
          // Don't throw error as this is non-critical functionality
        }
      } else {
        console.log('[GAME] Skipping player interactions logging for social/private session');
      }
      
      // Optionally create partnership_games records
      try {
        // This function is no longer needed as partnership_games are created automatically
        // by the database trigger when player_pair_game_history records are inserted
        // Partnership games are created automatically by database trigger
      } catch (error) {
        console.warn('[PARTNERSHIP_GAMES] Failed to create partnership games (this is optional):', error);
        // Don't throw error as this is optional functionality
      }
    }
    return game;
  } catch (error) {
    console.error('[GAME] Error in createGameDirect:', error);
    // Return the game data even if player-game records failed
    // This allows the game to be saved even if the player-game records fail
    if (typeof game !== 'undefined' && game !== null) {
      console.warn('[GAME] Returning game despite error in createGameDirect');
      return game;
    }
    throw error;
  }
};

/**
 * Updates player ELO ratings in the players_club table
 * @param {Array} players - Array of players with updated ELO data
 * @param {string} clubId - Club ID
 */
export const updatePlayerElos = async (players, clubId, seasonId) => {
  try {
    console.log(`Updating ELOs for ${players.length} players in club ${clubId}`);
    for (const player of players) {
      // Check if player-club-season record exists
      const { data: existingRecord, error: checkError } = await supabase
        .from('player_club_elo_ratings')
        .select('id')
        .eq('player_id', player.id)
        .eq('club_id', clubId)
        .eq('season_id', seasonId)
        .single();
      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error checking existing player-club record:', checkError);
        throw checkError;
      }
      if (existingRecord) {
        // Update ELO fields for this season
        const { error: updateError } = await supabase
          .from('player_club_elo_ratings')
          .update({
            mixed_elo_rating: player.mixed_elo_rating,
            levels_elo_rating: player.levels_elo_rating,
            updated_at: new Date().toISOString()
          })
          .eq('player_id', player.id)
          .eq('club_id', clubId)
          .eq('season_id', seasonId);
        if (updateError) {
          console.error('Error updating player-club record:', updateError);
          throw updateError;
        }
      } else {
        // Insert new record for this season
        const { error: insertError } = await supabase
          .from('player_club_elo_ratings')
          .insert([
            {
              player_id: player.id,
              club_id: clubId,
              season_id: seasonId,
              mixed_elo_rating: player.mixed_elo_rating,
              levels_elo_rating: player.levels_elo_rating,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ]);
        if (insertError) {
          console.error('Error creating player-club record:', insertError);
          throw insertError;
        }
      }
    }
    console.log('All player ELOs updated successfully');
  } catch (error) {
    console.error('Error updating player ELOs:', error);
    throw error;
  }
};

/**
 * Creates partnership_games records for a session game
 * @param {string} sessionGameId - The session game ID
 * @returns {Promise<Object>} Result
 */
// This function is no longer needed as partnership_games are created automatically
// by the database trigger when player_pair_game_history records are inserted
export const createPartnershipGames = async (sessionGameId) => {
  console.log('[PARTNERSHIP_GAMES] Partnership games are now created automatically by database trigger');
  return { success: true, message: 'Partnership games created automatically' };
};

/**
 * Calculates partnership game stats for a specific partnership and game
 * @param {string} partnershipId - The partnership ID
 * @param {string} gameId - The game ID
 * @returns {Promise<Object>} Result
 */
// This function is no longer needed as partnership_game_stats are calculated automatically
// by the database trigger when partnership_games records are created
export const calculatePartnershipGameStats = async (partnershipId, gameId) => {
  console.log('[PARTNERSHIP_STATS] Partnership game stats are now calculated automatically by database trigger');
  return { success: true, message: 'Partnership game stats calculated automatically' };
};

// Add players to a session (activate), with optional status.
// Idempotent: skips players already on the session (e.g. after signup migration trigger on start).
export const addPlayersToSession = async (sessionId, playerIds, clubId, status = 'signed_up') => {
  const ids = (playerIds || []).filter(Boolean);
  if (!ids.length) return;

  const records = ids.map((player_id) => ({
    session_id: sessionId,
    player_id,
    club_id: clubId,
    status,
    exit_reason: null, // New players don't have an exit reason
  }));

  const { error } = await supabase
    .from('session_players')
    .upsert(records, { onConflict: 'session_id,player_id', ignoreDuplicates: true });

  if (error) throw error;
};

// Remove players from a session (deactivate)
export const removePlayersFromSession = async (sessionId, playerIds) => {
  const { error } = await supabase.from('session_players')
    .delete()
    .eq('session_id', sessionId)
    .in('player_id', playerIds);
  if (error) throw error;
};

/**
 * Get suggested late joiner penalty based on current session state.
 * Only suggests a penalty if average games played >= 1 (session has started).
 * 
 * @param {string} sessionId - Session ID
 * @param {string} playerId - Player ID being activated
 * @returns {Object} { suggestedPenalty, averageGames, shouldShowModal }
 */
export const getSuggestedLatePenalty = async (sessionId, playerId) => {
  try {
    const [{ data: activatingPlayerRow }, { data: activePlayers }] = await Promise.all([
      supabase
        .from('session_players')
        .select('games_played')
        .eq('session_id', sessionId)
        .eq('player_id', playerId)
        .maybeSingle(),
      supabase
        .from('session_players')
        .select('player_id')
        .eq('session_id', sessionId)
        .eq('status', 'active')
        .neq('player_id', playerId),
    ]);

    const playerGamesPlayed = activatingPlayerRow?.games_played ?? 0;

    if (!activePlayers || activePlayers.length === 0) {
      return {
        suggestedPenalty: 0,
        averageGames: 0,
        shouldShowModal: false,
        playerGamesPlayed,
        isReturningPlayer: playerGamesPlayed > 0,
      };
    }
    
    // Get effective games played count for all active players (actual games + penalties)
    const activePlayerIds = activePlayers.map(p => p.player_id);
    
    // Get actual games played from session_games
    const { data: sessionGames } = await supabase
      .from('session_games')
      .select('id')
      .eq('session_id', sessionId);
    
    if (!sessionGames || sessionGames.length === 0) {
      return {
        suggestedPenalty: 0,
        averageGames: 0,
        shouldShowModal: false,
        playerGamesPlayed,
        isReturningPlayer: playerGamesPlayed > 0,
      };
    }
    
    const sessionGameIds = sessionGames.map(g => g.id);
    
    const { data: gamesData } = await supabase
      .from('players_session_games')
      .select('player_id')
      .in('player_id', activePlayerIds)
      .in('session_game_id', sessionGameIds);
    
    if (!gamesData || gamesData.length === 0) {
      return {
        suggestedPenalty: 0,
        averageGames: 0,
        shouldShowModal: false,
        playerGamesPlayed,
        isReturningPlayer: playerGamesPlayed > 0,
      };
    }
    
    // Count actual games per player
    const actualGamesPerPlayer = {};
    gamesData.forEach(game => {
      actualGamesPerPlayer[game.player_id] = (actualGamesPerPlayer[game.player_id] || 0) + 1;
    });
    
    // Get existing penalties for active players
    const { data: penaltyData } = await supabase
      .from('session_players')
      .select('player_id, late_joiner_penalty')
      .eq('session_id', sessionId)
      .in('player_id', activePlayerIds);
    
    // Calculate effective games (actual + penalties) for each player
    const effectiveGamesPerPlayer = {};
    activePlayerIds.forEach(playerId => {
      const actualGames = actualGamesPerPlayer[playerId] || 0;
      const penalty = penaltyData?.find(p => p.player_id === playerId)?.late_joiner_penalty || 0;
      effectiveGamesPerPlayer[playerId] = actualGames + penalty;
    });
    
    // Calculate average effective games played
    const totalEffectiveGames = Object.values(effectiveGamesPerPlayer).reduce((sum, count) => sum + count, 0);
    const averageGames = totalEffectiveGames / activePlayers.length;
    const suggestion = computeLateJoinerPenaltySuggestion({ averageGames, playerGamesPlayed });

    return {
      suggestedPenalty: suggestion.suggestedPenalty,
      averageGames,
      shouldShowModal: suggestion.shouldShowModal,
      playerGamesPlayed: suggestion.playerGamesPlayed,
      isReturningPlayer: suggestion.isReturningPlayer,
    };
  } catch (error) {
    console.error('Error calculating suggested penalty:', error);
    return {
      suggestedPenalty: 0,
      averageGames: 0,
      shouldShowModal: false,
      playerGamesPlayed: 0,
      isReturningPlayer: false,
    };
  }
};

/**
 * Manually set late joiner penalty for a player.
 * This is the ONLY way penalties should be set (no automatic calculation).
 * 
 * @param {string} sessionId - Session ID
 * @param {string} playerId - Player ID
 * @param {number} penalty - Penalty value (games count)
 * @returns {Object} { success, penalty }
 */
export const setManualLatePenalty = async (sessionId, playerId, penalty) => {
  const { error } = await supabase
    .from('session_players')
    .update({ late_joiner_penalty: penalty })
    .eq('session_id', sessionId)
    .eq('player_id', playerId);
  
  if (error) throw error;
  return { success: true, penalty };
};

export const updateSessionPlayerStatus = async (sessionId, playerId, status, exitReason = null) => {
  // Get current player status to check if we need to update activated_at
  const { data: currentPlayer, error: fetchError } = await supabase
    .from('session_players')
    .select('status, activated_at')
    .eq('session_id', sessionId)
    .eq('player_id', playerId)
    .single();
  
  if (fetchError) throw fetchError;
  
  const updateData = { status };
  
  // Set exit_reason based on the new status
  if (status === 'inactive') {
    // If becoming inactive, we need an exit reason
    updateData.exit_reason = exitReason || 'finished'; // Default to 'finished' if not specified
  } else if (status === 'cancelled') {
    // If marking as cancelled, set exit reason to cancelled
    updateData.exit_reason = 'cancelled';
  } else {
    // If becoming active/paused/signed_up, clear exit reason
    updateData.exit_reason = null;
  }
  
  // Update activated_at when player becomes active (but not if they were just paused)
  if (status === 'active' && currentPlayer.status !== 'paused') {
    updateData.activated_at = new Date().toISOString();
  }
  
  console.log('updateSessionPlayerStatus - updating database:', { sessionId, playerId, updateData });
  
  const { error } = await supabase.from('session_players')
    .update(updateData)
    .eq('session_id', sessionId)
    .eq('player_id', playerId);
  if (error) throw error;
  
  console.log('updateSessionPlayerStatus - database update successful');
  
  // Simplified return - no automatic penalty calculation
  return { success: true };
};

// Get all players in a session (with status and exit_reason)
export const getSessionPlayers = async (sessionId) => {
  const { data, error } = await supabase
    .from('session_players')
    .select('player_id, status, exit_reason, late_joiner_penalty, players(*)')
    .eq('session_id', sessionId);
  if (error) throw error;
  return data;
};

/**
 * Updates the session_status for a session
 * @param {string} sessionId - The session ID
 * @param {string} status - The new status (e.g., 'COMPLETED', 'CANCELLED')
 * @returns {Promise<void>}
 */
export const updateSessionStatus = async (sessionId, status) => {
  try {
    console.log(`[SESSION] Updating session ${sessionId} status to ${status}`);
    
    // Validate status
    const validStatuses = ['ongoing', 'completed', 'cancelled'];
    const normalizedStatus = status.toLowerCase();
    
    if (!validStatuses.includes(normalizedStatus)) {
      console.warn(`[SESSION] Invalid status: ${status}. Using 'completed' instead.`);
      status = 'completed';
    }
    
    // RPC sets a 120s local statement_timeout so completion triggers can finish on hosted PostgREST
    const { data, error: sessionError } = await supabase.rpc('set_session_status', {
      p_session_id: sessionId,
      p_status: normalizedStatus,
    });
    
    if (sessionError) {
      console.error('[SESSION] Error updating session status:', sessionError);
      throw sessionError;
    }

    if (normalizedStatus === 'completed') {
      triggerSessionCompletionEffects(sessionId);
    }
    
    console.log(`[SESSION] Successfully updated session status:`, data);
    return data;
  } catch (error) {
    console.error('[SESSION] Error updating session status:', error);
    throw error;
  }
};

/**
 * Checks for and completes any sessions that were left in 'ongoing' status
 * This can be called when the app starts to clean up sessions that weren't properly ended
 * @param {string} clubId - The club ID to check sessions for
 * @param {number} hoursThreshold - Sessions older than this many hours will be marked as completed
 * @returns {Promise<Object>} - Information about sessions that were cleaned up
 */
export const cleanupIncompleteSessions = async (clubId, hoursThreshold = 24) => {
  try {
    console.log(`[SESSION] Checking for incomplete sessions older than ${hoursThreshold} hours`);
    
    // Calculate the cutoff time (current time minus threshold hours)
    const cutoffTime = new Date();
    cutoffTime.setHours(cutoffTime.getHours() - hoursThreshold);
    const cutoffTimeString = cutoffTime.toISOString();
    
    // Find sessions that are still 'ongoing' but were created before the cutoff time
    const { data: incompleteSessions, error: findError } = await supabase
      .from('sessions')
      .select('id, name, session_date')
      .eq('club_id', clubId)
      .eq('session_status', 'ongoing')
      .lt('session_date', cutoffTimeString);
    
    if (findError) {
      console.error('[SESSION] Error finding incomplete sessions:', findError);
      throw findError;
    }
    
    if (!incompleteSessions || incompleteSessions.length === 0) {
      console.log('[SESSION] No incomplete sessions found');
      return { cleaned: 0, sessions: [] };
    }
    
    console.log(`[SESSION] Found ${incompleteSessions.length} incomplete sessions to clean up`);
    
    // Update all incomplete sessions to 'completed'
    const sessionIds = incompleteSessions.map(s => s.id);
    const { data: updatedData, error: updateError } = await supabase
      .from('sessions')
      .update({ session_status: 'completed' })
      .in('id', sessionIds)
      .select();
    
    if (updateError) {
      console.error('[SESSION] Error updating incomplete sessions:', updateError);
      throw updateError;
    }
    
    console.log(`[SESSION] Successfully cleaned up ${updatedData.length} sessions`);
    return { 
      cleaned: updatedData.length, 
      sessions: incompleteSessions.map(s => ({
        id: s.id,
        name: s.name,
        date: s.session_date
      }))
    };
  } catch (error) {
    console.error('[SESSION] Error in cleanupIncompleteSessions:', error);
    throw error;
  }
};

/**
 * Create a free play game during a session
 * @param {Object} gameData - Game data
 * @param {string} gameData.sessionId - Session ID
 * @param {string} gameData.clubId - Club ID
 * @param {Array} gameData.team1Players - Team 1 players
 * @param {Array} gameData.team2Players - Team 2 players
 * @param {Object} gameData.score - Score object
 * @param {Object} playersById - Players by ID
 * @returns {Promise<Object>} Created game
 */
export const createFreePlayGame = async (gameData, playersById) => {
  const gameTypeId = detectGameType(gameData.team1Players, gameData.team2Players).id;
  
  const fullGameData = {
    ...gameData,
    gameCreateMethod: 'self_reported'
  };
  
  return createGame(fullGameData, playersById, false);
};

/**
 * Create an external game (outside of app sessions)
 * @param {Object} gameData - Game data
 * @param {Array} gameData.team1Players - Team 1 players
 * @param {Array} gameData.team2Players - Team 2 players
 * @param {Object} gameData.score - Score object
 * @param {string} [gameData.clubId] - Optional club ID if known
 * @param {Object} playersById - Players by ID
 * @returns {Promise<Object>} Created game
 */
export const createExternalGame = async (gameData, playersById) => {
  const gameTypeId = detectGameType(gameData.team1Players, gameData.team2Players).id;
  
  const fullGameData = {
    ...gameData,
    sessionId: null,  // No session
    clubId: gameData.clubId || null,
    seasonId: null,   // No season
    gameCreateMethod: 'self_reported',
    startTime: gameData.startTime || new Date().toISOString(),
    endTime: gameData.endTime || null
  };
  
  return createGame(fullGameData, playersById, false);
};

/**
 * Check if a game should affect ELO ratings
 * Note: This assumes competitive session context. The database function
 * game_affects_elo() performs the full check including session type.
 * 
 * @param {string} gameCreateMethod - The game_create_method value
 * @returns {boolean} True if game affects ELO (in competitive sessions)
 */
export const gameAffectsElo = (gameCreateMethod) => {
  if (!gameCreateMethod) return false;
  
  // self_reported games never affect ELO
  if (gameCreateMethod === 'self_reported') {
    return false;
  }
  
  // These methods affect ELO in competitive sessions
  const eloAffectingMethods = [
    'matchmaking',
    'matchmaking_altered',
    'scheduled',
    'scheduled_altered',
    'authoritative'  // Host/admin created games in competitive sessions
  ];
  
  return eloAffectingMethods.includes(gameCreateMethod);
};

/**
 * Delete a game (for self_reported games only)
 * Removes the game and all related records (players_session_games, etc.)
 * @param {string} gameId - The game ID to delete
 * @returns {Promise<void>}
 */
export const deleteGame = async (gameId) => {
  try {
    // First, verify the game is self_reported (safety check)
    const { data: game, error: gameError } = await supabase
      .from('session_games')
      .select('game_create_method')
      .eq('id', gameId)
      .single();

    if (gameError) {
      throw new Error(`Game not found: ${gameError.message}`);
    }

    if (game.game_create_method !== 'self_reported') {
      throw new Error('Only self-reported games can be deleted');
    }

    // Delete related records first (CASCADE should handle this, but being explicit)
    // Delete players_session_games
    const { error: playersError } = await supabase
      .from('players_session_games')
      .delete()
      .eq('session_game_id', gameId);

    if (playersError) {
      console.warn('Error deleting players_session_games:', playersError);
      // Continue anyway as CASCADE should handle it
    }

    // Delete game contexts
    const { error: contextsError } = await supabase
      .from('game_contexts')
      .delete()
      .eq('game_id', gameId);

    if (contextsError) {
      console.warn('Error deleting game_contexts:', contextsError);
      // Continue anyway
    }

    // Delete the game itself
    const { error: deleteError } = await supabase
      .from('session_games')
      .delete()
      .eq('id', gameId);

    if (deleteError) {
      throw new Error(`Failed to delete game: ${deleteError.message}`);
    }

    console.log(`✅ Game ${gameId} deleted successfully`);
  } catch (error) {
    console.error('Error deleting game:', error);
    throw error;
  }
};

/**
 * Update a game (for self_reported games only)
 * @param {string} gameId - The game ID to update
 * @param {Object} updates - Updates to apply
 * @param {number} [updates.team_1_score] - Team 1 score
 * @param {number} [updates.team_2_score] - Team 2 score
 * @param {number} [updates.winning_team] - Winning team (1 or 2)
 * @param {Object} playersById - Players by ID (for player updates)
 * @param {Array} [updates.team1Players] - New team 1 players
 * @param {Array} [updates.team2Players] - New team 2 players
 * @returns {Promise<Object>} Updated game data
 */
export const updateGame = async (gameId, updates, playersById = {}) => {
  try {
    // First, verify the game is self_reported (safety check)
    const { data: game, error: gameError } = await supabase
      .from('session_games')
      .select('game_create_method')
      .eq('id', gameId)
      .single();

    if (gameError) {
      throw new Error(`Game not found: ${gameError.message}`);
    }

    if (game.game_create_method !== 'self_reported') {
      throw new Error('Only self-reported games can be updated');
    }

    // Calculate winning team if scores are provided
    let winningTeam = updates.winning_team;
    if (updates.team_1_score !== undefined && updates.team_2_score !== undefined) {
      if (updates.team_1_score === updates.team_2_score) {
        throw new Error('Scores cannot be equal');
      }
      winningTeam = updates.team_1_score > updates.team_2_score ? 1 : 2;
    }

    // Update game record
    const gameUpdates = {};
    if (updates.team_1_score !== undefined) gameUpdates.team_1_score = updates.team_1_score;
    if (updates.team_2_score !== undefined) gameUpdates.team_2_score = updates.team_2_score;
    if (winningTeam !== undefined) gameUpdates.winning_team = winningTeam;

    if (Object.keys(gameUpdates).length > 0) {
      const { error: updateError } = await supabase
        .from('session_games')
        .update(gameUpdates)
        .eq('id', gameId);

      if (updateError) {
        throw new Error(`Failed to update game: ${updateError.message}`);
      }
    }

    // If players are being updated, we need to update players_session_games
    // This is more complex - for now, we'll just update scores
    // Full player update would require deleting and recreating players_session_games records
    // TODO: Implement full player update if needed

    console.log(`✅ Game ${gameId} updated successfully`);
    
    // Fetch and return updated game
    const { data: updatedGame, error: fetchError } = await supabase
      .from('session_games')
      .select('*')
      .eq('id', gameId)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch updated game: ${fetchError.message}`);
    }

    return updatedGame;
  } catch (error) {
    console.error('Error updating game:', error);
    throw error;
  }
};
